import { ArrowType, LineType } from '@ardoq/api-types';
import { RepresentationData } from '@ardoq/data-model';
import { Bounds, UrlFieldValue, Vector } from '@ardoq/graph';
import { Projection } from './projection';
import { DebouncedFunc } from 'lodash';
import { DragHandler } from './interactions/types';

export type BlocksLinksAggregation =
  | 'hide'
  | 'separate'
  | 'byReferenceType'
  | 'all';

export type BlocksPlacementStyle = {
  nodeMinimumSize: [number, number];
  nodeChannelMinimumSize: number;
  routingChannelMinimumSize: number;
  routingPad: number;
  routingSep: number;
};

export type BlocksLayoutType =
  | 'process'
  | 'compact'
  | 'seriesparallel'
  | 'grid'
  | 'manual';

// these enumeration values deliberately mimic their yfiles homologues
export type BlocksLayoutOrientation =
  | 'top-to-bottom'
  | 'left-to-right'
  | 'bottom-to-top'
  | 'right-to-left';

export enum BlocksVisualState {
  None = 0,
  Hover = 1,
  Select = 2,
  Highlight = 4,
  Hidden = 8,
}

export type ItemSet = {
  add: (item: BlocksNode | BlocksReference) => void;
  delete: (item: BlocksNode | BlocksReference) => void;
  has: (item: BlocksNode | BlocksReference) => boolean;
  clear: () => void;
  values(): IterableIterator<BlocksNode | BlocksReference>;
  readonly size: number;
  isSubsetOf(itemSet: ItemSet): boolean;

  node(): BlocksNode | null;
  nodes(): Generator<BlocksNode, void, unknown>;
  link(): BlocksReference | null;
  links(): Generator<BlocksReference, void, unknown>;
  bounds(): Bounds;
};

export type BlocksAnimation = {
  begin: number;
  from: Bounds | Vector[] | null;
  duration: number;
};

export type NodeLayoutData = {
  col: number;
  colSpan: number;
  row: number;
  rowSpan: number;
  open?: boolean;
};

export type BlocksItem = {
  modelId: string | null; // this is required payload
  color: string; // nodes specify a generic "color" attribute which the component uses to modulate the display
  labels: string[]; // main label, submain label then sublabels
  labelColors: (string | undefined)[]; // labels specify a generic "color" attribute which the component will display in an appropriate way
  urlFieldValues: UrlFieldValue[] | null;
  visualState: BlocksVisualState;
  animation?: BlocksAnimation;
};

export type BlocksNode = BlocksItem & {
  readonly isComponent: boolean; // not used by me

  /* structural and descriptive */

  readonly id: string; // unique data id
  /* readonly */ parent: BlocksNode | null; // some clients set this
  children: BlocksNode[] | null;
  readonly links: BlocksReference[] | null;

  readonly isContext: boolean; // this one seems like an odd-man-out
  readonly representationData: RepresentationData | null;

  /* layout data */

  open?: boolean; // group node is open
  readonly isSynthetic?: boolean; // group is synthetic

  /**
   * A cell the caller is asking for, rather than one layout worked out.
   *
   * The distinction, not the field, is the point. `layoutData` below is an *output*: every layout
   * writes it, `mergeGraph` clears it on a reused node so the position cannot outlive the content
   * that justified it, and `placeNodes` reads it to index the grid. A caller with a position in
   * mind had nowhere to put it and so wrote into that same field - which works right up until
   * something has to tell the two apart, and `mergeGraph` is exactly that something. It cannot ask
   * "did anyone ask for this cell, or did I compute it last frame?", so clearing the field loses
   * the request and keeping it keeps a stale result.
   *
   * Read by the `fixupLocal*Layout` pass, which seeds from it and then writes `layoutData` as
   * usual. Untouched by the merge, because an input arrives with the data it belongs to.
   *
   * Any layout can honour these, and only some nodes need to carry one - which is the useful shape
   * rather than an accident of it. "Lay this out, but that node goes there" is a different request
   * from "every node's position is given", and one field expresses both.
   *
   * Not `readonly`, for the same reason `parent` above is not. The caller writes it, and it cannot
   * always do so at construction: a *global* cell is not knowable until the whole tree has been
   * measured, so a producer builds the nodes and then walks them assigning cells. The contract
   * lives in this comment rather than in the type - written by whoever supplies the graph, read by
   * layout, never written by layout.
   */
  requestedCell?: NodeLayoutData;

  layoutData?: NodeLayoutData; // node columns and rows or possibly undefined

  /* ephemeral bits used by the view */

  displayLabels: string[][]; // wrapped and truncated version of label
  width: number; // item preferred width (comes off node style; ignored for open group items)
  height: number; // item preferred height (comes off node style; ignored for open group items) */
  isLayoutDirty?: boolean; // group item has dirty layout content
  layoutType?: BlocksLayoutType; // group item layout override
  cell?: Bounds; // world cell rectangle
  bounds: Bounds; // world bounds rectangle
  content?: Bounds; // open group child area
  rowSize?: number[]; // height of group node rows;
  colSize?: number[]; // width of group node columns;
};

type RequireOptionalKeys<T, K extends keyof T> = Omit<T, K> & {
  [P in K]-?: NonNullable<T[P]>;
};
export type BlocksGroup = RequireOptionalKeys<BlocksNode, 'children'>;

export type BlocksReference = BlocksItem & {
  readonly source: BlocksNode;
  readonly target: BlocksNode;

  readonly lineBeginning: ArrowType;
  readonly lineEnding: ArrowType;
  readonly lineType: LineType;

  globalTypeId: string | null;

  bounds?: Bounds;
  route?: Vector[];
  sourceProxy?: BlocksNode;
  targetProxy?: BlocksNode;
  labelSegment?: number;
};

export type BlocksGraph = {
  layoutType?: BlocksLayoutType;
  layoutOrientation?: BlocksLayoutOrientation;

  root: BlocksNode;
  edges: BlocksReference[];
};

export type LayoutData = {};

export type OnTooltip =
  | undefined
  | ((component: AqBlocks, items: ItemSet, position: Vector) => () => void);

export type OnContextMenu =
  | undefined
  | ((component: AqBlocks, items: ItemSet, position: Vector) => () => void);

export type BlocksLayoutStyle = {
  type: BlocksLayoutType;
  orientation: BlocksLayoutOrientation;
  linksAggregation: BlocksLinksAggregation;
};

// #region Selected event
export type BlocksSelectedDetail = {
  readonly graph?: BlocksGraph;
  readonly items: ItemSet;
};

export class BlocksSelectedEvent extends CustomEvent<BlocksSelectedDetail> {
  static readonly type = 'Selected';

  constructor(detail: BlocksSelectedDetail) {
    super(BlocksSelectedEvent.type, {
      detail,
      bubbles: true,
      cancelable: true,
    });
  }
}
// #endregion

// #region Hovered event
export type BlocksHoveredDetail = {
  readonly graph?: BlocksGraph;
  readonly items: ItemSet;
};

export class BlocksHoveredEvent extends CustomEvent<BlocksHoveredDetail> {
  static readonly type = 'Hovered';

  constructor(detail: BlocksHoveredDetail) {
    super(BlocksHoveredEvent.type, {
      detail,
      bubbles: true,
      cancelable: true,
    });
  }
}
// #endregion

// #region edited event
export type BlocksEditedDetail = {
  readonly graph?: BlocksGraph;
  // there could be one or more nodes edited
  // there could be one or more nodes with colateral damage
};
export class BlocksEditedEvent extends CustomEvent<BlocksEditedDetail> {
  static readonly type = 'Edited';

  constructor(detail: BlocksEditedDetail) {
    super(BlocksEditedEvent.type, {
      detail,
      bubbles: true,
      cancelable: true,
    });
  }
}
// #endregion

// #region activated event
export type BlocksActivatedDetail = {
  readonly graph?: BlocksGraph;
  readonly items: ItemSet;
};

export type BlocksFindRequestedDetail = {
  readonly graph: BlocksGraph | null;
};

/** The find shortcut, Ctrl+F or Cmd+F, was pressed on the diagram. */
export class BlocksFindRequestedEvent extends CustomEvent<BlocksFindRequestedDetail> {
  static readonly type = 'FindRequested';

  constructor(detail: BlocksFindRequestedDetail) {
    super(BlocksFindRequestedEvent.type, {
      detail,
      bubbles: true,
      cancelable: true,
    });
  }
}

export class BlocksActivatedEvent extends CustomEvent<BlocksActivatedDetail> {
  static readonly type = 'Activated';

  constructor(detail: BlocksActivatedDetail) {
    super(BlocksActivatedEvent.type, {
      detail,
      bubbles: true,
      cancelable: true,
    });
  }
}
// #endregion

export type AqBlocksEventMap = {
  Selected: BlocksSelectedEvent;
  Hovered: BlocksHoveredEvent;
  Edited: BlocksEditedEvent;
  Activated: BlocksActivatedEvent;
};

export type ExportRenderOptions = {
  /** export pixels per world unit */
  scale: number;
  /** whitespace around the diagram, in export pixels */
  margin: number;
};

export interface AqBlocks extends HTMLElement {
  activate(items: ItemSet): void;

  expandAll(): void;
  collapseAll(): void;
  toggleExpanded(groups?: BlocksNode[]): void;
  expand(groups?: BlocksNode[]): void;
  collapse(groups?: BlocksNode[]): void;

  zoomFit(): void;
  zoomIn(): void;
  zoomOut(): void;

  panLeft(): void;
  panUp(): void;
  panRight(): void;
  panDown(): void;

  flipHorizontal(): void;
  flipVertical(): void;
  rotate(): void;

  actionGroups(): BlocksNode[];

  adjustToRect(bounds: Bounds, margin?: number): void;
  zoomBy(scale: number, center?: Vector): void;
  panTo(wd: Vector, vp: Vector): void;

  // #region user-settable properties
  isUserSelectable: boolean;
  isReadonly: boolean;
  isDisabled: boolean;

  graph: BlocksGraph | null;
  layoutStyle: BlocksLayoutStyle;
  placementStyle: BlocksPlacementStyle;

  doContextMenu: OnContextMenu;
  doTooltip: OnTooltip;

  setGroupLayoutType(groups: BlocksNode[], layoutType: BlocksLayoutType): void;
  // #endregion

  // #region context menu and tooltips
  showContextMenu(items: ItemSet, client: Vector): void;
  cancelContextMenu(): void;

  pendTooltip(): void;
  cancelTooltip(): void;
  // #endregion

  // #region selection state
  /** Items currently under the pointer. Read-only from outside. */
  readonly hovered: ItemSet;
  readonly updateHovered: DebouncedFunc<() => void>;

  selected: ItemSet;
  highlighted: ItemSet;
  // #endregion

  // #region canvas, projection, render
  readonly autoPan: DebouncedFunc<() => void>;
  pendRender(): void;
  getExportContentSize(): Vector | undefined;
  getExportSize(options: ExportRenderOptions): Vector | undefined;
  renderExport(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    options: ExportRenderOptions
  ): boolean;
  // #endregion

  // #region pointer state (mutated by the input pipeline; rarely useful externally)
  cancel(): void;
  pointerViewport: Vector;
  pointerDownViewport: Vector;
  pointer: Vector;
  pointerDown: Vector;
  dragHandler: DragHandler | null;
  readonly pointerCapture: number;
  readonly canvas: HTMLCanvasElement | null;
  readonly projection: Projection;
  readonly isFocused: boolean;

  // #endregion

  // #region event listener typing
  addEventListener<K extends keyof AqBlocksEventMap>(
    type: K,
    listener: (this: AqBlocks, ev: AqBlocksEventMap[K]) => unknown,
    options?: boolean | AddEventListenerOptions
  ): void;
  removeEventListener<K extends keyof AqBlocksEventMap>(
    type: K,
    listener: (this: AqBlocks, ev: AqBlocksEventMap[K]) => unknown,
    options?: boolean | EventListenerOptions
  ): void;
  // #endregion
}
